import { useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';

import { ApiError, api } from '@/api/client';
import type { GeneratePreview, PatternSaved, ScheduleView } from '@/api/types';
import { useApi } from '@/api/useApi';
import {
  Button,
  Caption,
  Card,
  CardHead,
  ErrorState,
  Field,
  Loading,
  Notice,
  Page,
  PageHead,
  Table,
  Tag,
  TextInput,
} from '@/components/ui';
import { formatDayIn, formatTimeIn, localDateIn } from '@/lib/format';

/**
 * Who covers a client's week, and the visits that follow from it.
 *
 * Two steps, deliberately separate. Staffing the pattern says who normally
 * covers Monday mornings; generating turns that into bookings. Keeping them
 * apart is what lets a coordinator fix next month's cover without silently
 * rewriting a visit a caregiver has already been told about.
 *
 * Every time on this screen is rendered in the agency's zone, not the
 * browser's. A visit at 09:00 in the client's kitchen has to read 09:00 on the
 * screen that books it, or someone working away from the office rosters an
 * hour out and the screen agrees with them.
 */

const DAY_NAMES: Record<string, string> = {
  mon: 'Monday',
  tue: 'Tuesday',
  wed: 'Wednesday',
  thu: 'Thursday',
  fri: 'Friday',
  sat: 'Saturday',
  sun: 'Sunday',
};

export function SchedulePage() {
  const { id } = useParams<{ id: string }>();
  const { data, error, loading, reload } = useApi<ScheduleView>(`/agency/clients/${id}/schedule`);

  // Only on the first load. Reloading after a save would otherwise unmount the
  // whole page, which throws away the "saved" confirmation the coordinator just
  // earned and flashes the screen on every action.
  if (loading && !data) return <Loading />;
  if (error)
    return (
      <Page>
        <ErrorState message={error.message} onRetry={reload} />
      </Page>
    );
  if (!data) return null;

  return (
    <Page>
      <PageHead
        breadcrumb={
          <nav className="breadcrumb" aria-label="Breadcrumb">
            <Link to="/clients">Clients</Link>
            <span aria-hidden="true">/</span>
            <Link to={`/clients/${id}`}>{data.client.name}</Link>
            <span aria-hidden="true">/</span>
            <span>Schedule</span>
          </nav>
        }
        title={`${data.client.name} — schedule`}
        subtitle={
          data.plan
            ? `Care plan v${data.plan.version_number}${
                data.plan.end_date ? ` · care ends ${data.plan.end_date}` : ''
              } · times shown in ${data.timezone.replace('_', ' ')}`
            : 'No published care plan'
        }
        actions={
          <Button variant="secondary" onClick={() => window.location.assign(`/clients/${id}/plan`)}>
            Open care plan
          </Button>
        }
      />

      {data.plan === null ? (
        <Notice tone="attention" title="This client has no published care plan">
          A schedule is built from the pattern a published plan authorises. Publish the plan
          first, then come back to staff it.
        </Notice>
      ) : data.pattern.length === 0 ? (
        <Notice tone="attention" title="This plan does not say when care happens">
          The care plan has no schedule blocks, so there is no pattern to staff. Add a schedule
          to the plan and it will appear here.
        </Notice>
      ) : (
        <>
          {/*
            Keyed on which blocks exist, not on their staffing. An ordinary
            save keeps the key, so the editor stays mounted and its "saved"
            notice survives the reload. Republishing the plan with a different
            week changes the key and remounts it, so the draft cannot linger
            against blocks that no longer exist.
          */}
          <PatternEditor
            key={data.pattern.map((block) => block.key).join('|')}
            view={data}
            clientId={id!}
            onSaved={reload}
          />
          <Generator view={data} clientId={id!} onGenerated={reload} />
        </>
      )}

      {/*
        Shown above the pattern, not inside it. An empty dropdown is easy to
        read past; a count is not, and the coordinator needs it before they
        generate rather than after.
      */}
      {data.uncovered_blocks > 0 ? (
        <Notice
          tone="attention"
          title={`${data.uncovered_blocks} of ${data.pattern.length} visits a week have nobody on them`}
        >
          Those visits will not be booked. The care plan says they should happen —
          assign someone below, or change the plan if the care is no longer needed.
        </Notice>
      ) : null}

      {/*
        Publishing a plan changes what care is agreed and leaves everything
        already booked exactly where it was. Without this a caregiver turns up
        to deliver care the current plan does not cover, and nothing anywhere
        says so.
      */}
      {data.visits_outside_plan > 0 ? (
        <OutsidePlan view={data} clientId={id!} onChanged={reload} />
      ) : null}

      {data.orphaned_assignments.length > 0 ? (
        <Notice tone="attention" title="Some staffing no longer matches the plan">
          <p>
            The plan's schedule changed, so these assignments are for blocks it no longer
            contains. They are ignored and will not generate visits.
          </p>
          <ul>
            {data.orphaned_assignments.map((orphan, index) => (
              <li key={index}>
                {DAY_NAMES[orphan.day] ?? orphan.day} {orphan.start}–{orphan.end} —{' '}
                {orphan.caregiver_name}
              </li>
            ))}
          </ul>
        </Notice>
      ) : null}

      <BookedVisits view={data} onChanged={reload} />
    </Page>
  );
}

/** Step one: who covers each block the plan authorises. */
function PatternEditor({
  view,
  clientId,
  onSaved,
}: {
  view: ScheduleView;
  clientId: string;
  onSaved: () => void;
}) {
  const [draft, setDraft] = useState<Record<string, string | null>>(() =>
    Object.fromEntries(view.pattern.map((block) => [block.key, block.caregiver_id])),
  );
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [saved, setSaved] = useState<PatternSaved | null>(null);

  const dirty = view.pattern.some((block) => draft[block.key] !== block.caregiver_id);
  const staffed = view.pattern.filter((block) => draft[block.key]).length;

  async function save() {
    setBusy(true);
    setProblem(null);
    try {
      const result = await api.put<PatternSaved>(
        `/agency/clients/${clientId}/schedule/pattern`,
        {
          blocks: view.pattern.map((block) => ({
            day: block.day,
            start: block.start,
            end: block.end,
            caregiver_id: draft[block.key] ?? null,
          })),
        },
      );
      setSaved(result);
      onSaved();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead
        title="Who covers each visit"
        actions={
          <Button onClick={save} busy={busy} disabled={!dirty}>
            Save who covers
          </Button>
        }
      />
      <Caption>
        The care plan says when care happens. This says who delivers it. Changing someone here
        also moves the visits already booked for that block — but never one that has started,
        and never one you have sent somebody else to cover.
      </Caption>

      {problem ? <Notice tone="urgent" title="That did not save">{problem}</Notice> : null}
      {saved && !dirty && !problem ? (
        <Notice tone="positive" title={`${staffed} of ${view.pattern.length} blocks staffed`}>
          {/*
            Moving somebody else's booked visits is not a silent side effect —
            if the save reassigned work, the screen says so and how much.
          */}
          {saved.visits_moved > 0 ? (
            <p>
              {saved.visits_moved} booked visit{saved.visits_moved === 1 ? '' : 's'} moved to the
              new caregiver.
            </p>
          ) : null}
          {saved.visits_unassigned > 0 ? (
            <p>
              {saved.visits_unassigned} booked visit
              {saved.visits_unassigned === 1 ? ' is' : 's are'} now unassigned — still owed, with
              nobody on them.
            </p>
          ) : null}
          {saved.visits_moved === 0 && saved.visits_unassigned === 0 ? (
            <p>Saved. Book the visits below to put them on the calendar.</p>
          ) : null}
        </Notice>
      ) : null}

      <Table columns={['When', 'Caregiver']} widths={['40%', '60%']}>
        {view.pattern.map((block) => (
          <tr key={block.key}>
            <td>
              <strong>{DAY_NAMES[block.day] ?? block.day}</strong>
              <br />
              <span className="meta">
                {block.start}–{block.end}
              </span>
            </td>
            <td>
              <select
                aria-label={`Caregiver for ${DAY_NAMES[block.day] ?? block.day} ${block.start}`}
                className="input"
                value={draft[block.key] ?? ''}
                onChange={(event) =>
                  setDraft((current) => ({
                    ...current,
                    [block.key]: event.target.value || null,
                  }))
                }
              >
                <option value="">Nobody yet</option>
                {view.caregivers.map((caregiver) => (
                  <option key={caregiver.id} value={caregiver.id}>
                    {caregiver.name}
                    {caregiver.status === 'invited' ? ' (invite not accepted)' : ''}
                  </option>
                ))}
              </select>
            </td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}

/** Step two: turn the staffed pattern into bookings. */
function Generator({
  view,
  clientId,
  onGenerated,
}: {
  view: ScheduleView;
  clientId: string;
  onGenerated: () => void;
}) {
  const today = useMemo(() => localDateIn(new Date(), view.timezone), [view.timezone]);
  const defaultEnd = useMemo(() => {
    const date = new Date();
    date.setDate(date.getDate() + 28);
    const fourWeeks = localDateIn(date, view.timezone);
    // Never offer to schedule past the date care was agreed to end.
    return view.plan?.end_date && view.plan.end_date < fourWeeks
      ? view.plan.end_date
      : fourWeeks;
  }, [view.timezone, view.plan]);

  const [from, setFrom] = useState(today);
  const [through, setThrough] = useState(defaultEnd);
  const [preview, setPreview] = useState<GeneratePreview | null>(null);
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [result, setResult] = useState<GeneratePreview | null>(null);

  const staffed = view.pattern.filter((block) => block.caregiver_id).length;

  async function run(dryRun: boolean) {
    setBusy(true);
    setProblem(null);
    try {
      const response = await api.post<GeneratePreview>(
        `/agency/clients/${clientId}/schedule/visits`,
        { start_date: from, end_date: through, preview: dryRun },
      );
      if (dryRun) {
        setPreview(response);
        setResult(null);
      } else {
        setResult(response);
        setPreview(null);
        onGenerated();
      }
    } catch (caught) {
      setProblem((caught as ApiError).message);
      setPreview(null);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Card>
      <CardHead title="Book the visits" />
      {/*
        Say what the button does. The first person to use this screen asked
        what "put the visits on the calendar" meant, which is a fair question
        when the copy describes the gesture rather than the effect — and the
        effect matters here, because a visit is also what gives a caregiver
        access to the client at all.
      */}
      <Caption>
        {staffed === 0
          ? 'Nobody is on this client’s week yet. Assign caregivers above first.'
          : `Creates one visit for each time a staffed block comes round between these two dates — ${staffed} a week, from the pattern above. Those visits are what the assigned caregiver sees in their app, and what lets them open this client's plan. Anything already booked is left alone, so this is safe to run twice.`}
        {view.plan?.end_date
          ? ` Nothing is booked past ${view.plan.end_date}, when care is agreed to end.`
          : ''}
      </Caption>

      {/*
        row--top, not the default centring. A Field renders its hint between
        the label and the input, so a hint on one of two side-by-side fields
        pushes that input down — and centring then made the two boxes different
        widths as well. Aligning to the top and giving both the same basis keeps
        them identical whatever is written above them.

        The end date is deliberately not a hint here. It is a fact about the
        plan rather than guidance for filling the box, it is already in the page
        subtitle, and `max` below enforces it.
      */}
      <div className="row row--wrap row--top">
        <div style={{ flex: '0 1 200px' }}>
          <Field label="From">
            {(props) => (
              <TextInput
                {...props}
                type="date"
                value={from}
                min={today}
                onChange={(event) => {
                  setFrom(event.target.value);
                  setPreview(null);
                }}
              />
            )}
          </Field>
        </div>
        <div style={{ flex: '0 1 200px' }}>
          <Field label="Through">
            {(props) => (
              <TextInput
                {...props}
                type="date"
                value={through}
                min={from}
                max={view.plan?.end_date ?? undefined}
                onChange={(event) => {
                  setThrough(event.target.value);
                  setPreview(null);
                }}
              />
            )}
          </Field>
        </div>
      </div>

      {problem ? (
        <Notice tone="urgent" title="Nothing was scheduled">
          {problem}
        </Notice>
      ) : null}

      {preview ? (
        <Notice
          tone={preview.would_create ? 'info' : 'attention'}
          title={
            preview.would_create
              ? `${preview.would_create} visit${preview.would_create === 1 ? '' : 's'} to book`
              : 'Nothing new to book in that range'
          }
        >
          {preview.already_booked > 0 ? (
            <p>{preview.already_booked} already on the calendar and left as they are.</p>
          ) : null}
          {preview.capped_by_plan_end ? (
            <p>Stops at {view.plan?.end_date}, when care is agreed to end.</p>
          ) : null}
          {preview.first ? (
            <p>
              First {formatDayIn(preview.first, view.timezone)}, last{' '}
              {formatDayIn(preview.last, view.timezone)}.
            </p>
          ) : null}
          <Uncovered uncovered={preview.uncovered} />
          <Conflicts conflicts={preview.conflicts} timezone={view.timezone} />
        </Notice>
      ) : null}

      {result ? (
        <Notice tone="positive" title={`${result.created} visit${result.created === 1 ? '' : 's'} booked`}>
          {result.already_booked > 0 ? (
            <p>{result.already_booked} were already on the calendar.</p>
          ) : null}
          <Uncovered uncovered={result.uncovered} />
          <Conflicts conflicts={result.conflicts} timezone={view.timezone} />
        </Notice>
      ) : null}

      <div className="row row--wrap" style={{ gap: 'var(--s-6)' }}>
        <Button variant="secondary" onClick={() => run(true)} busy={busy} disabled={staffed === 0}>
          Show me what it would book
        </Button>
        <Button onClick={() => run(false)} busy={busy} disabled={staffed === 0}>
          Book these visits
        </Button>
      </div>
    </Card>
  );
}

/**
 * The visits the plan no longer covers, and one way to deal with all of them.
 *
 * Dropping a day from a plan with a season booked strands a dozen visits, and
 * cancelling them one at a time is the kind of chore that gets half-done —
 * leaving four stranded instead of twelve, and no record of which were
 * deliberate. So the whole set is one action.
 *
 * It asks first, and it says the number, because this cancels visits
 * caregivers have already been rostered onto.
 */
function OutsidePlan({
  view,
  clientId,
  onChanged,
}: {
  view: ScheduleView;
  clientId: string;
  onChanged: () => void;
}) {
  const [busy, setBusy] = useState(false);
  const [problem, setProblem] = useState<string | null>(null);
  const [confirming, setConfirming] = useState(false);

  const stranded = view.visits.filter((visit) => visit.outside_plan);

  async function cancelAll() {
    setBusy(true);
    setProblem(null);
    try {
      const result = await api.post<{ cancelled: number; not_cancelled: { id: string }[] }>(
        `/agency/clients/${clientId}/schedule/visits/cancel-outside-plan`,
        { visit_ids: stranded.map((visit) => visit.id) },
      );
      if (result.not_cancelled.length > 0) {
        // Not an error — the plan or the roster moved under them. Saying so is
        // better than a silent partial result.
        setProblem(
          `${result.cancelled} cancelled. ${result.not_cancelled.length} were left: ` +
            'the plan now covers them again, or somebody changed them while this was open.',
        );
      }
      setConfirming(false);
      onChanged();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Notice
      tone="urgent"
      title={`${stranded.length} booked visit${stranded.length === 1 ? '' : 's'} the care plan no longer covers`}
    >
      <p>
        The plan changed and these were already booked, so they are still
        scheduled and still assigned. They are marked below.
      </p>
      {problem ? <p>{problem}</p> : null}
      {confirming ? (
        <>
          <p>
            <strong>
              Cancel {stranded.length} visit{stranded.length === 1 ? '' : 's'}?
            </strong>{' '}
            Any caregiver already rostered onto them will lose the visit. This
            cannot be undone from here.
          </p>
          <div className="row row--wrap" style={{ gap: 'var(--s-6)' }}>
            <Button variant="secondary" onClick={() => setConfirming(false)} disabled={busy}>
              Keep them
            </Button>
            <Button onClick={() => void cancelAll()} busy={busy}>
              Yes, cancel {stranded.length}
            </Button>
          </div>
        </>
      ) : (
        <Button variant="secondary" onClick={() => setConfirming(true)}>
          Cancel the {stranded.length} the plan does not cover
        </Button>
      )}
    </Notice>
  );
}

/**
 * The visits this run will not book, and why.
 *
 * "12 visits booked" reads as finished. If two of the week's blocks have
 * nobody on them, the month is not covered and the number alone says the
 * opposite — so the gap is named next to the success, not instead of it.
 */
function Uncovered({ uncovered }: { uncovered: GeneratePreview['uncovered'] }) {
  if (!uncovered || uncovered.length === 0) return null;
  return (
    <>
      <p>
        <strong>
          {uncovered.length} visit{uncovered.length === 1 ? '' : 's'} a week will not be
          booked — nobody covers {uncovered.length === 1 ? 'it' : 'them'}:
        </strong>
      </p>
      <ul>
        {uncovered.map((block, index) => (
          <li key={index}>
            {DAY_NAMES[block.day] ?? block.day} {block.start}–{block.end}
          </li>
        ))}
      </ul>
    </>
  );
}

/**
 * Clashes are shown, not enforced. A caregiver finishing one client and
 * starting the next at the same minute is a normal round, so back-to-back is
 * not reported at all — only a genuine overlap reaches here, and the
 * coordinator knows things the system does not.
 */
function Conflicts({
  conflicts,
  timezone,
}: {
  conflicts: GeneratePreview['conflicts'];
  timezone: string;
}) {
  if (conflicts.length === 0) return null;
  return (
    <>
      <p>
        <strong>
          {conflicts.length} clash{conflicts.length === 1 ? '' : 'es'} with other clients:
        </strong>
      </p>
      <ul>
        {conflicts.slice(0, 8).map((conflict, index) => (
          <li key={index}>
            {conflict.caregiver_name} is already with {conflict.client_name} on{' '}
            {formatDayIn(conflict.clashing_start, timezone)} at{' '}
            {formatTimeIn(conflict.clashing_start, timezone)}
          </li>
        ))}
      </ul>
      {conflicts.length > 8 ? <Caption>…and {conflicts.length - 8} more.</Caption> : null}
    </>
  );
}

/** What is actually booked, with per-visit cover and cancellation. */
function BookedVisits({ view, onChanged }: { view: ScheduleView; onChanged: () => void }) {
  const [busyId, setBusyId] = useState<string | null>(null);
  const [problem, setProblem] = useState<string | null>(null);

  const upcoming = view.visits.filter((visit) => visit.state !== 'cancelled');

  async function reassign(visitId: string, caregiverId: string) {
    if (!caregiverId) return;
    setBusyId(visitId);
    setProblem(null);
    try {
      await api.put(`/agency/visits/${visitId}/caregiver`, { caregiver_id: caregiverId });
      onChanged();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusyId(null);
    }
  }

  async function cancel(visitId: string) {
    setBusyId(visitId);
    setProblem(null);
    try {
      await api.post(`/agency/visits/${visitId}/cancel`, {});
      onChanged();
    } catch (caught) {
      setProblem((caught as ApiError).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <Card>
      <CardHead
        title="Booked visits"
        actions={
          view.scheduled_through ? (
            <Caption>Covered through {formatDayIn(view.scheduled_through, view.timezone)}</Caption>
          ) : null
        }
      />
      {problem ? <Notice tone="urgent" title="That did not work">{problem}</Notice> : null}

      <Table
        columns={['When', 'Caregiver', 'Change', '']}
        widths={['28%', '30%', '30%', '12%']}
        empty="Nothing booked yet. Staff the week above, then generate the visits."
      >
        {upcoming.map((visit) => (
          <tr key={visit.id}>
            <td>
              <strong>{formatDayIn(visit.start, view.timezone)}</strong>
              <br />
              <span className="meta">
                {formatTimeIn(visit.start, view.timezone)}–
                {formatTimeIn(visit.end, view.timezone)}
              </span>
            </td>
            <td>
              {visit.outside_plan ? (
                <>
                  <Tag label="not in the plan" tone="urgent" />{' '}
                </>
              ) : null}
              {visit.caregiver_name ?? <span className="meta">Unassigned</span>}
              {visit.covering_for ? (
                <>
                  {' '}
                  <Tag label={`covering for ${visit.covering_for}`} tone="neutral" />
                </>
              ) : null}
              {visit.has_note ? (
                <>
                  {' '}
                  <Tag label="note written" tone="positive" />
                </>
              ) : null}
            </td>
            <td>
              <select
                aria-label={`Send someone else on ${formatDayIn(visit.start, view.timezone)}`}
                className="input"
                value=""
                disabled={busyId === visit.id || visit.state !== 'scheduled'}
                onChange={(event) => void reassign(visit.id, event.target.value)}
              >
                <option value="">Send someone else…</option>
                {view.caregivers
                  .filter((caregiver) => caregiver.id !== visit.caregiver_id)
                  .map((caregiver) => (
                    <option key={caregiver.id} value={caregiver.id}>
                      {caregiver.name}
                    </option>
                  ))}
              </select>
            </td>
            <td>
              <Button
                variant="ghost"
                busy={busyId === visit.id}
                disabled={visit.state !== 'scheduled'}
                onClick={() => void cancel(visit.id)}
              >
                Cancel
              </Button>
            </td>
          </tr>
        ))}
      </Table>
    </Card>
  );
}
